import React, { useRef, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  LinearProgress,
  FormHelperText,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import type { UploadStatus } from '../types';
import {
  MAX_FILE_SIZE,
  MAX_FILES,
  MAX_TOTAL_SIZE,
  ACCEPTED_FILE_TYPES,
} from '../constants/fileUpload';

export interface PerFileMetadataConfig {
  key: string;
  label: string;
  options: ReadonlyArray<{ value: string; label: string }>;
  required?: boolean;
}

export function fileKey(file: File): string {
  return `${file.name}-${file.size}`;
}

export interface FileUploadZoneProps {
  files: File[];
  onChange: (files: File[]) => void;
  maxFileSize?: number;
  maxFiles?: number;
  maxTotalSize?: number;
  acceptedTypes?: readonly string[];
  uploadProgress?: Map<string, number>;
  uploadStatus?: Map<string, UploadStatus>;
  error?: string;
  disabled?: boolean;
  label?: string;
  required?: boolean;
  helperText?: string;
  /** When provided, each staged-file row renders a metadata Select column. */
  perFileMetadata?: PerFileMetadataConfig;
  /** Map keyed by `fileKey(file)` → selected metadata value. */
  metadataValues?: Record<string, string>;
  /** Invoked with (fileKey, value) when the user picks a metadata value. */
  onMetadataChange?: (fileKey: string, value: string) => void;
  /** Render slot inserted between the drop zone (and its helper/error text)
   *  and the staged-file list. Always renders when supplied, regardless of
   *  whether files are staged. Used by `UploadDocumentsModal` to host a
   *  batch-level metadata Select. */
  stagedFilesSlotBefore?: React.ReactNode;
}

function getFileIcon(mimeType: string) {
  if (mimeType === 'application/pdf') {
    return <PictureAsPdfIcon sx={{ color: 'error.main' }} />;
  }
  if (mimeType.startsWith('image/')) {
    return <ImageIcon sx={{ color: 'info.main' }} />;
  }
  if (mimeType.includes('word') || mimeType.includes('document')) {
    return <DescriptionIcon sx={{ color: 'primary.main' }} />;
  }
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) {
    return <TableChartIcon sx={{ color: 'success.main' }} />;
  }
  return <InsertDriveFileIcon sx={{ color: 'text.secondary' }} />;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

interface FileListItemProps {
  file: File;
  index: number;
  onRemove: (index: number) => void;
  progress?: number;
  status?: UploadStatus;
  disabled?: boolean;
  perFileMetadata?: PerFileMetadataConfig;
  metadataValue?: string;
  onMetadataChange?: (fileKey: string, value: string) => void;
}

const FileListItem = React.memo(function FileListItem({
  file,
  index,
  onRemove,
  progress,
  status,
  disabled,
  perFileMetadata,
  metadataValue,
  onMetadataChange,
}: FileListItemProps) {
  const isUploading = status === 'uploading';
  const isCompleted = status === 'completed';
  const isFailed = status === 'failed';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        bgcolor: 'background.paper',
        borderRadius: 1,
        border: '1px solid',
        borderColor: isFailed ? 'error.main' : 'divider',
      }}
    >
      {getFileIcon(file.type)}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {file.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatFileSize(file.size)}
        </Typography>
        {isUploading && progress !== undefined && (
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
          />
        )}
      </Box>
      {perFileMetadata && (
        <FormControl size="small" sx={{ width: 220, flexShrink: 0 }}>
          <Select
            value={metadataValue ?? ''}
            onChange={(e) => onMetadataChange?.(fileKey(file), String(e.target.value))}
            disabled={disabled}
            displayEmpty
            inputProps={{ 'aria-label': `${perFileMetadata.label} for ${file.name}` }}
            sx={{
              height: 36,
              fontSize: 13,
              bgcolor: 'background.paper',
              borderRadius: '4px',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
            }}
          >
            <MenuItem value="" sx={{ fontSize: 13, color: 'text.secondary' }}>
              {`Select ${perFileMetadata.label}`}
            </MenuItem>
            {perFileMetadata.options.map((opt) => (
              <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 13 }}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {isCompleted && (
        <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
      )}
      {isFailed && (
        <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />
      )}
      {!isUploading && !isCompleted && (
        <IconButton
          size="small"
          onClick={() => onRemove(index)}
          disabled={disabled}
          aria-label={`Remove ${file.name}`}
          sx={{ color: 'text.secondary' }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
});

export default function FileUploadZone({
  files,
  onChange,
  maxFileSize = MAX_FILE_SIZE,
  maxFiles = MAX_FILES,
  maxTotalSize = MAX_TOTAL_SIZE,
  acceptedTypes = ACCEPTED_FILE_TYPES,
  uploadProgress,
  uploadStatus,
  error,
  disabled = false,
  label = 'Supporting Documents',
  required = false,
  helperText,
  perFileMetadata,
  metadataValues,
  onMetadataChange,
  stagedFilesSlotBefore,
}: FileUploadZoneProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const validateFiles = useCallback(
    (newFiles: File[]): { valid: File[]; error: string | null } => {
      const validFiles: File[] = [];
      const currentTotalSize = files.reduce((sum, f) => sum + f.size, 0);
      let runningTotal = currentTotalSize;

      for (const file of newFiles) {
        // Check file type
        if (!acceptedTypes.includes(file.type)) {
          return {
            valid: [],
            error: `${file.name} is not a supported file type. Accepted: PDF, JPG, PNG, DOC, DOCX, XLS, XLSX`,
          };
        }

        // Check individual file size
        if (file.size > maxFileSize) {
          return {
            valid: [],
            error: `${file.name} exceeds the ${formatFileSize(maxFileSize)} limit`,
          };
        }

        // Check file count
        if (files.length + validFiles.length >= maxFiles) {
          return {
            valid: validFiles,
            error: `Maximum ${maxFiles} files allowed`,
          };
        }

        // Check total size
        if (runningTotal + file.size > maxTotalSize) {
          return {
            valid: validFiles,
            error: `Total file size exceeds ${formatFileSize(maxTotalSize)} limit`,
          };
        }

        validFiles.push(file);
        runningTotal += file.size;
      }

      return { valid: validFiles, error: null };
    },
    [files, maxFileSize, maxFiles, maxTotalSize, acceptedTypes]
  );

  const handleFiles = useCallback(
    (newFiles: FileList | File[]) => {
      if (disabled) return;

      const fileArray = Array.from(newFiles);
      const { valid, error: validationErr } = validateFiles(fileArray);

      setValidationError(validationErr);

      if (valid.length > 0) {
        onChange([...files, ...valid]);
      }
    },
    [disabled, validateFiles, onChange, files]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        setIsDragOver(true);
      }
    },
    [disabled]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      if (disabled) return;

      const droppedFiles = e.dataTransfer.files;
      if (droppedFiles.length > 0) {
        handleFiles(droppedFiles);
      }
    },
    [disabled, handleFiles]
  );

  const handleClick = useCallback(() => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [disabled]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = e.target.files;
      if (selectedFiles && selectedFiles.length > 0) {
        handleFiles(selectedFiles);
      }
      // Reset input so same file can be selected again
      e.target.value = '';
    },
    [handleFiles]
  );

  const handleRemove = useCallback(
    (index: number) => {
      const newFiles = files.filter((_, i) => i !== index);
      onChange(newFiles);
      setValidationError(null);
    },
    [files, onChange]
  );

  const displayError = error || validationError;
  const acceptString = acceptedTypes.join(',');

  return (
    <Box>
      {/* Label — suppressed when `label` is empty so callers can supply their
          own heading (e.g. a SectionHeading above the zone). */}
      {label && (
        <Typography
          component="label"
          sx={{
            display: 'block',
            mb: 1,
            fontWeight: 500,
            fontSize: 14,
            color: 'text.primary',
          }}
        >
          {label}
          {required && (
            <Typography component="span" sx={{ color: 'error.main', ml: 0.5 }}>
              *
            </Typography>
          )}
        </Typography>
      )}

      {/* Drop Zone */}
      <Box
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          p: 3,
          minHeight: 120,
          border: '2px dashed',
          borderColor: displayError
            ? 'error.main'
            : isDragOver
              ? 'primary.main'
              : 'divider',
          borderRadius: 2,
          bgcolor: isDragOver ? 'primary.50' : 'background.default',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          transition: 'all 0.2s ease-in-out',
          '&:hover': disabled
            ? {}
            : {
                borderColor: 'primary.main',
                bgcolor: 'primary.50',
              },
        }}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label="Upload files"
        aria-disabled={disabled}
      >
        <CloudUploadIcon
          sx={{ fontSize: 40, color: isDragOver ? 'primary.main' : 'text.secondary' }}
        />
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Drag and drop files here, or click to browse
        </Typography>
        <Typography variant="caption" color="text.secondary">
          PDF, JPG, PNG, DOC, DOCX, XLS, XLSX (max {formatFileSize(maxFileSize)} per file)
        </Typography>
      </Box>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={acceptString}
        onChange={handleInputChange}
        style={{ display: 'none' }}
        disabled={disabled}
        aria-hidden="true"
        data-testid="file-input"
      />

      {/* Error Message */}
      {displayError && (
        <FormHelperText error sx={{ mt: 1 }}>
          {displayError}
        </FormHelperText>
      )}

      {/* Helper Text */}
      {helperText && !displayError && (
        <FormHelperText sx={{ mt: 1 }}>{helperText}</FormHelperText>
      )}

      {/* Slot rendered between drop zone (and helper/error text) and the
       *  staged-file list. Always renders when provided, even with zero
       *  staged files. Used to host a batch-level metadata Select. */}
      {stagedFilesSlotBefore}

      {/* File List */}
      {files.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
          {files.map((file, index) => {
            const key = `${file.name}-${file.size}`;
            return (
              <FileListItem
                key={`${key}-${index}`}
                file={file}
                index={index}
                onRemove={handleRemove}
                progress={uploadProgress?.get(key)}
                status={uploadStatus?.get(key)}
                disabled={disabled}
                perFileMetadata={perFileMetadata}
                metadataValue={metadataValues?.[key]}
                onMetadataChange={onMetadataChange}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
}
